/**
 * This library holds all the functions related to Mobile Pixels Warranties. 
 * In order for this system to fire off, please enable the global setting in the theme editor.
 * 
 * System handles the work for: 
 *      Showing / hiding the Warranty Modals
 *      Adding the warranty to the front end as selectable options
 *      Adding the product to the cart with a warranty
 *      Adding the product to the cart without a warranty
 *      Adding the product to the cart when no warranty options are found
 *      Removing a bundled parent product from the cart
 *      Removing a child product from a bundled product
 * 
 * 
 * DEBUG LOG COLOR KEY:
 *      #ADD8E6 = Object was Built
 *      #FFF720 = Event was Attached
 *      #34b233 = Object was Added to DOM
 *      #FFA500 = Action Notification
 * 
 * V 1.20 Updates
 *      - Updated BUY NOW Button to use same codebase as ATC
 *      - Updated warranty modal to redirect to Checkout when BUY NOW Clicked
 * 
 * @author Ethan Tuttle - Text Connects
 * @version 1.10 - 05/17/2023
 * @version 1.20 - 07/13/2023
 */

import { AjaxCart } from "//www.mobilepixels.us/cdn/shop/t/310/assets/theme.js?v=97297886699544766711750905220";

const debugLogging = false;

document.addEventListener('DOMContentLoaded', main__MobilePixelsWarranty);
/**
 * Function will be responsible for handling all functionality after the DOM has loaded
 * @returns {void}
 */
function main__MobilePixelsWarranty() {
  loadInformationModal(debugLogging);

  // Get modal
  const warrantyCoveragePlan = document.querySelector('#Warranty-Coverage-Plan');
  const warrantyOptionsPromise = fetchWarrantyOptions();
  warrantyOptionsPromise.then(result => {
    let resultAsObject = JSON.parse(result);

    if (resultAsObject.message == 'unauthorized') {
      resultAsObject.coverage_options = [];
    }

    if (resultAsObject.coverage_options.length <= 0) {
      if (debugLogging) {
        console.log('%cNothing found for coverage', 'color: #FFA500');
      }

      // hide warranty coverage plan
      if (warrantyCoveragePlan != null && warrantyCoveragePlan != undefined) {
        warrantyCoveragePlan.classList.add('hidden');
      }

      // loadDefaultAddToCartBehavior(debugLogging);
    } else {
      loadWarranyInfoIntoButtons(result, debugLogging);
      // loadWarrantyUpdateTriggers( debugLogging );

      if (warrantyCoveragePlan != null && warrantyCoveragePlan != undefined) {
        warrantyCoveragePlan.classList.remove('hidden');
      }

      // loadWarrantyModalTriggers(debugLogging);
      loadWarrantyModalSelectionTriggers(debugLogging);
    }
  }).catch(e => console.error(e));

  addAllWarrantyOptionsToLocalStorage();

  return;
}

/*************************************************************
 * UTILITY FUNCTIONS
 * Any functions that handle logic that can be reused multiple 
 * times. These functions are not specific to building HTML, 
 * Event Handlers, And Event Instantiations.
*************************************************************/

/**
 * Function fetches the current warranty options based on the product id and variant Id selected.
 * @returns {Promise} response - The response from the Fetch Call to the API
 */
async function fetchWarrantyOptions(product_id = null, variant_id = null) {
  //Build URL
  // if the productDetails dont exist
  if (typeof (window._productDetails) === 'undefined') {
    return '{ "coverage_options": [] }';
  }

  let APIUrl = "";

  if (product_id != null && variant_id != null) {
    APIUrl = `/a/warranty/product-database-product/${product_id}/variant/${variant_id}`;
  } else {
    APIUrl = `/a/warranty/product-database-product/${window._productDetails.product_id}/variant/${window._productDetails.currently_selected_variant_id}`;
  }

  // Make a call to the API
  var myHeaders = new Headers();
  var authHeaderString = `mobilepixels.myshopify.com:${API_LOGIN_KEY}`;
  myHeaders.append("Authorization", authHeaderString);

  var requestOptions = {
    method: 'GET',
    headers: myHeaders
  };

  const response = fetch(APIUrl, requestOptions).then(response => response.text()).then(result => {
    const jsonResponse = JSON.parse(result);

    // add warranty info to local storage
    const localStorageWarranty = localStorage.getItem('warranties');
    if (localStorageWarranty !== null) {
      const jsonItems = JSON.parse(localStorageWarranty);

      // console.log(localStorageWarranty.includes(result))

      if (jsonResponse.coverage_options.length != 0 && !localStorageWarranty.includes(result)) {
        const newItemsForStorage = [...jsonItems, JSON.parse(result)];  // response is returned as text.  Must convert to JSON
        localStorage.setItem('warranties', JSON.stringify(newItemsForStorage));
      }
    } else {
      if (jsonResponse.coverage_options.length != 0) {
        const tempArr = [];
        tempArr.push(JSON.parse(result))  // response is returned as text.  Must convert to JSON
        localStorage.setItem('warranties', JSON.stringify(tempArr))
      }
    }
    return result;
  });

  return response;
}

/**
 * Required for Bundle Upgrade.  We need all of the warranty options for the upgraded product to be available in local storage so a warranty can be added after upgrade.
 * When a customer goes to a PDP, all variant IDs are added to the window._productDetails object (theme.liquid)
 * This function gets the warranty data for every variant other than the currenly selected variant (that happens in the above function)
 * Everything is stored to Local Storage
 */
async function addAllWarrantyOptionsToLocalStorage() {
  if (typeof (window._productDetails) === 'undefined') {
    return '{ "coverage_options": [] }';
  }

  let allVariants = window._productDetails.variants.split(",");

  // querySelector Popular-bundles
  const popularBundles = document.querySelector('.popular-bundles');
  if (popularBundles != null) {
    const bundleItems = popularBundles.querySelectorAll('.popular-bundles__item');
    if (bundleItems != null) {
      for (let i = 0; i < bundleItems.length; i++) {
        let bundleItem = bundleItems[i];
        let bundleProductId = bundleItem.getAttribute('data-product-id');
        let bundleVariantId = bundleItem.getAttribute('data-variant-id');

        allVariants.push({
          productId: bundleProductId,
          variantId: bundleVariantId
        });
      }
    }
  }

  for (let variant of allVariants) {
    let variantId = !isNaN(Number(variant)) ? Number(variant) : variant.variantId;
    let APIUrl = `/a/warranty/product-database-product/${!isNaN(Number(variant)) ? window._productDetails.product_id : variant.productId}/variant/${variantId}`;

    // Make a call to the API
    var myHeaders = new Headers();
    var authHeaderString = `mobilepixels.myshopify.com:${API_LOGIN_KEY}`;
    myHeaders.append("Authorization", authHeaderString);

    var requestOptions = {
      method: 'GET',
      headers: myHeaders
    };

    fetch(APIUrl, requestOptions).then(response => response.text()).then(result => {
      const jsonResponse = JSON.parse(result);

      // add warranty info to local storage
      const localStorageWarranty = localStorage.getItem('warranties');
      if (localStorageWarranty !== null) {
        const jsonItems = JSON.parse(localStorageWarranty);

        if (jsonResponse.coverage_options.length != 0 && !localStorageWarranty.includes(result)) {
          const newItemsForStorage = [...jsonItems, JSON.parse(result)];  // response is returned as text.  Must convert to JSON
          localStorage.setItem('warranties', JSON.stringify(newItemsForStorage));
        }
      } else {
        if (jsonResponse.coverage_options.length != 0) {
          const tempArr = [];
          tempArr.push(JSON.parse(result))  // response is returned as text.  Must convert to JSON
          localStorage.setItem('warranties', JSON.stringify(tempArr))
        }
      }
    });
  }

}

/**
 * Function gets the currently checked option element based on what input is currently checked
 * @returns {HTMLElement} optionElement - The checked option element for either the outer or inner radio buttons
 */
function getWarrantyOptionSelected() {
  const modalWarrantyOptions = document.querySelector('.protection-plan-options-wrapper');

  let optionElement = null;

  let checkedEl = null;

  // if the modal was opened from the warranty upsell button in cart, set the option to the one selected in the modal
  if (modalWarrantyOptions != null && modalWarrantyOptions != undefined) {
    checkedEl = modalWarrantyOptions.querySelector('input:checked');
  }

  if (checkedEl != null) {
    optionElement = checkedEl;
  }

  return optionElement;
}

/*************************************************************
 * BUILD FUNCTIONS
 * Any functions that handle logic related to building dynamic 
 * HTML elements.
*************************************************************/

/**
 * Function takes the coverage options found and builds the HTML for the front end.
 * The HTML is then outputted to the Upsell modal.
 * @param {Array} coverageOptions - The warranty coverage options found for the specific product / variant id
 * @param {HTMLElement} debug - determines if console.log() statements show up, default is false
 * @returns {void} 
 */
function buildInnerLabels(coverageOptions, debug = false) {
  coverageOptions.sort((a, b) => {
    let trueLengthA = parseInt(a.plan_length.split(' ')[0]);
    let trueLengthB = parseInt(b.plan_length.split(' ')[0]);

    if (trueLengthA > trueLengthB) {
      return 1;
    }
    else {
      return -1;
    }
  });

  for (let i = 0; i < coverageOptions.length; i++) {
    // Label for input element
    let labelElement = document.createElement('label');
    labelElement.setAttribute('for', `warranty-upsell-option-${i}`);

    //Input Element
    let inputElement = document.createElement('input');
    inputElement.setAttribute('name', 'warranty-upsell');
    inputElement.setAttribute('id', `warranty-upsell-option-${i}`);
    inputElement.value = "";
    inputElement.setAttribute('type', 'radio');
    inputElement.setAttribute('data-plan-option', coverageOptions[i].title);
    inputElement.setAttribute('data-plan-variant-id', coverageOptions[i].id);
    inputElement.setAttribute('data-plan-product-id', coverageOptions[i].productId);
    inputElement.setAttribute('data-length', coverageOptions[i].plan_length);

    if (i == Math.round((coverageOptions.length - 1) / 2)) {
      inputElement.setAttribute('checked', '');
    }

    //Content Wrapper
    let planDetailsWrapper = document.createElement('div');
    planDetailsWrapper.classList.add('warranty-upsell-button-content');

    // Length
    let planLengthElement = document.createElement('div');
    planLengthElement.classList.add('plan-length');
    planLengthElement.innerHTML = coverageOptions[i].plan_length;

    //Plan Type 
    let planType = document.createElement('div');
    planType.classList.add('plan-type');
    planType.innerHTML = 'Protection Plan';

    //Price 
    let planPriceElement = document.createElement('div');
    planPriceElement.classList.add('plan-price');
    planPriceElement.innerHTML = '$' + coverageOptions[i].plan_price;

    // Badge - <div class="badge">Best Seller</div> | <div class="badge">Best Value</div>
    // Add Best Seller to middle option
    // Add Best Value to last option
    let badgeElement = document.createElement('div');
    badgeElement.classList.add('badge');

    if (i == Math.round((coverageOptions.length - 1) / 2)) {
      badgeElement.innerHTML = 'Best Seller';
    }

    if (i == coverageOptions.length - 1) {
      badgeElement.innerHTML = 'Best Value';
    }

    //combine details wrapper
    planDetailsWrapper.append(planLengthElement);
    planDetailsWrapper.append(planType);
    planDetailsWrapper.append(planPriceElement);

    // if we have a badge with content, add it
    if (badgeElement.innerHTML != '') {
      planDetailsWrapper.append(badgeElement);
    }

    //combine label
    labelElement.append(inputElement);
    labelElement.append(planDetailsWrapper);

    //append label element
    const modalOptionsWrapper = document.querySelector('.protection-plan-options-wrapper');
    if (modalOptionsWrapper != null && modalOptionsWrapper != undefined) {
      modalOptionsWrapper.append(labelElement);
    }

    if (debug) {
      console.log('%cInner Plan Option added: ', 'color: #34b233', labelElement);
    }
  }

  toggleLoading(false);
}

/**
 * Function takes the coverage options found and builds the HTML for the front end.
 * The HTML is then outputted to the Accident Protection point in the buy panel.
 * @param {Array} coverageOptions - The warranty coverage options found for the specific product / variant id
 * @param {HTMLElement} debug - determines if console.log() statements show up, default is false
 * @returns {void} 
 */
function buildOuterLabels(coverageOptions, debug = false) {
  if (debug) {
    console.log('building outer labels...');
  }
  coverageOptions.sort((a, b) => {
    let trueLengthA = parseInt(a.plan_length.split(' ')[0]);
    let trueLengthB = parseInt(b.plan_length.split(' ')[0]);

    if (trueLengthA > trueLengthB) {
      return 1;
    }
    else {
      return -1;
    }
  });

  for (let i = 0; i < coverageOptions.length; i++) {
    let labelElement = document.createElement('label');
    labelElement.setAttribute('for', `warranty-upsell-option-no-modal-${i}`);

    let inputElement = document.createElement('input');
    inputElement.setAttribute('name', 'warranty-upsell-no-modal');
    inputElement.setAttribute('id', `warranty-upsell-option-no-modal-${i}`);
    inputElement.value = "";
    inputElement.setAttribute('type', 'radio');
    inputElement.setAttribute('data-plan-option', coverageOptions[i].title);
    inputElement.setAttribute('data-plan-variant-id', coverageOptions[i].id);
    inputElement.setAttribute('data-plan-product-id', coverageOptions[i].productId);
    inputElement.setAttribute('data-length', coverageOptions[i].plan_length);

    let planDetailsWrapper = document.createElement('div');
    planDetailsWrapper.classList.add('plan-details');

    let planLengthElement = document.createElement('span');
    planLengthElement.classList.add('plan-length');
    planLengthElement.innerHTML = coverageOptions[i].plan_length;

    let dashElement = document.createElement('span');
    dashElement.innerHTML = ' - ';

    let planPriceElement = document.createElement('span');
    planPriceElement.classList.add('plan-price');
    planPriceElement.innerHTML = '$' + coverageOptions[i].plan_price;

    //combine details wrapper
    planDetailsWrapper.append(planLengthElement);
    planDetailsWrapper.append(dashElement);
    planDetailsWrapper.append(planPriceElement);

    //combine label
    labelElement.append(inputElement);
    labelElement.append(planDetailsWrapper);

    //append label element
    const buyPanelWarrantyPlanWrapper = document.querySelector('.warranty-plan-options');
    if (buyPanelWarrantyPlanWrapper != null && buyPanelWarrantyPlanWrapper != undefined) {
      buyPanelWarrantyPlanWrapper.append(labelElement);
    }

    if (debug) {
      console.log('%cOuter Plan Option added: ', 'color: #34b233', labelElement);
    }
  }

  return;
}

/*************************************************************
 * HANDLE FUNCTIONS
 * Any functions that handle logic related to event listner 
 * actions.
*************************************************************/

/**
 * Function handles Opening and closing a given Modal. The Upsell Modal has special checks to 
 * make sure we only open it when the outer options are not selected.
 * @param {Event} event - The Event Object from the triggered event
 * @param {currentItemsArray} array - The items needing to add to the cart (including primary product) - used when the BUY Button is utilized. Normally empty
 * @returns {void} 
 */
function handleModalToggle(event) {

  event.preventDefault();

  const eventTrigger = event.target;
  const infoModal = document.querySelector('#Warranty-Information-Modal');
  const upsellModal = document.querySelector('#Warranty-Upsell-Modal');

  // find out what our modal is
  let modalReference = null;
  if (eventTrigger.getAttribute('data-open-warranty-modal') === 'info' || eventTrigger.getAttribute('data-close-info-modal') === 'info' || eventTrigger.getAttribute('id') === 'Warranty-Information-Modal') {
    if (infoModal != null && infoModal != undefined) {
      modalReference = infoModal;
    }
  } else {
    if (upsellModal != null && upsellModal != undefined) {
      modalReference = upsellModal;
    }
  }

  // if the modal we are referencing is the upsell modal,
  // we need to verify that an option on the outer list wasnt already selected
  // if one was selected, we are going to trigger the accepted button inside the modal
  if (upsellModal != null && upsellModal != undefined) {
    if (modalReference == upsellModal) {
      const buyPanelWarrantyPlanWrapper = document.querySelector('.warranty-plan-options');
      //check the outer list for an option that is selected
      if (buyPanelWarrantyPlanWrapper !== null) {
        const selectableOptions = buyPanelWarrantyPlanWrapper.querySelectorAll('[data-plan-option]');

        let anOptionIsChecked = false;

        if (selectableOptions != null && selectableOptions != undefined) {
          for (let i = 0; i < selectableOptions.length; i++) {
            if (selectableOptions[i].checked) {
              anOptionIsChecked = true;
              break;
            }
          }
        }

        //if an outer option was check
        if (anOptionIsChecked) {
          const warrantyAcceptPlan = document.querySelector('[data-add-protection-plan]');

          if (warrantyAcceptPlan != null && warrantyAcceptPlan != undefined) {
            // check if the button triggering this event is the Close
            if (event.target !== warrantyAcceptPlan) {
              // trigger the warranty_accept_plan button
              if (debugLogging) { console.log('%cWarranty Plan already selected, adding that to the cart', 'color:  #FFA500'); }
              warrantyAcceptPlan.click();
            }
          }
        }
      }
    }
  }


  if (modalReference.classList.contains('closed')) {
    modalReference.classList.remove('closed');
    //  console.log('removed close')

    // const shadow = document.querySelector('cart-notification-drawer').shadowRoot;
    // let overlay = null;

    // if ( shadow !== null ) {
    //         console.log('hey they modal did this!')
    //         overlay = shadow.querySelector('[part="overlay"]');
    // }
    //  if ( overlay != null ) {
    //      setTimeout(() => {
    //         // console.log('Modal closed product overlay after 1 second')
    //         overlay.click();
    //     }, 1000)
    //  }
  } else {
    modalReference.classList.add('closed');
  }

  return;
}

/**
 * Function Handles The Offer Accepted and Offer Rejected Logic 
 * @param {Event} event - The Event Object from the triggered event
 * @returns {void} 
 */
function handleModalTriggers(event) {
  let debug = debugLogging;
  const warrantyCoveragePlan = document.querySelector('#Warranty-Coverage-Plan');
  const warrantyModal = document.querySelector("#Warranty-Upsell-Modal");

  let offerRejected = false;
  if (event.target.hasAttribute('data-no-protection-plan') || event.target.id == 'Warranty-Upsell-Modal') {
    offerRejected = true;
  }

  if (warrantyCoveragePlan !== null) {
    let errorMessageCur = warrantyCoveragePlan.querySelector('.error-message');
    if (errorMessageCur !== null) {
      errorMessageCur.remove();
    }
  }

  // get product's cart line item id from modal data attribute
  let linkedId = null;
  if (warrantyModal != null && warrantyModal != undefined) {
    if (warrantyModal.hasAttribute('data-opened-from-atc')) {
      linkedId = parseInt(warrantyModal.getAttribute('data-opened-from-atc'));
    } else if (warrantyModal.hasAttribute('data-opened-from-cart')) {
      linkedId = parseInt(warrantyModal.getAttribute('data-opened-from-cart'));
    }
  }

  // if the offer was rejected
  if (offerRejected) {
    if (debug) {
      console.log('%cOffer was rejected, add item to cart the normal way.', 'color:  #FFA500');
      console.log('%cClosing Modal...', 'color:  #FFA500');
      console.log('handle Modal Toggle Ran -- 1')
    }

    warrantyModal.classList.add('closed');

    return;
  }

  // Default state 
  if (debug) {
    console.log('%cOffer was accepted, add item to cart with warranty.', 'color:  #FFA500');
    console.log('%cClosing Modal...', 'color:  #FFA500');
    console.log('handle Modal Toggle Ran -- 2')
  }

  warrantyModal.classList.add('closed');

  // Assemble the cart Items Array
  if (linkedId != null) {
    let warrantyToAdd = null;

    // get warranty selected in modal
    const warrantySelected = getWarrantyOptionSelected();
    if (warrantySelected != null && warrantySelected != undefined) {
      if (warrantySelected.hasAttribute('data-plan-variant-id')) {
        warrantyToAdd = parseInt(warrantySelected.getAttribute('data-plan-variant-id'));
      }
    }

    // console.log('debugging: warranty to add', warrantyToAdd)
    if (!warrantyToAdd) return;
    
    AjaxCart.add([{
      id: warrantyToAdd,
      quantity: 1,
      properties: {
        _linked_id: linkedId,
        _is_mp_warranty: true,
        _unique_line_id: Date.parse(new Date())
      }
    }]).then((res) => {
      res.event();
    });
  }
}

/**
 * Function Handles The logic for removing a bundle from the cart
 * @param {Event} event - The Event Object from the triggered event
 * @param {HTMLElement} buttonClicked - The button element clicked
 * @param {Boolean} debug - determines if console.log() statements will show, default false
 * @returns {void} 
 */
function handleBundleRemoval(event) {
  event.preventDefault();
  let debug = debugLogging;
  let buttonClicked = event.currentTarget;
  const itemId = buttonClicked.getAttribute('data-id');

  let updates = { updates: {} }
  if (debug) {
    console.log('%cBundle key to hunt for: ', 'color: #ADD8E6', itemId);
    console.log('Handling Removal for: ', buttonClicked);
  }

  // if we clicked the remove button on MP protection plan..
  // otherwise we need to remove the parent and child.
  let isChild = false;
  if (buttonClicked.hasAttribute('data-is-child')) {
    isChild = true;
  }

  const getCart = theme.cart.getCart();
  getCart.then(cart => {
    let items = cart.items;
    let key = null;
    let qtyLimit = 1;
    for (let i = 0; i < items.length; i++) {
      key = items[i].key;

      if (key == itemId) {
        if (debug) {
          console.log('found matching keys! Key found: ', key);
        }
        updates.updates[key] = 0;
        qtyLimit = items[i].quantity;
      }
    }

    // if not a child, check for parent
    if (!isChild) {
      key = null;
      let linkedIdToFind = parseInt(itemId.split(':')[0]);
      if (debug) {
        console.log('running parent check for: ', linkedIdToFind);
      }

      let totalTimesAdded = 0;
      for (let i = 0; i < items.length; i++) {
        key = items[i].key;
        for (const [_key, value] of Object.entries(items[i].properties)) {
          if (_key == '_linked_id' && value == linkedIdToFind) {
            if (totalTimesAdded < qtyLimit) {
              updates.updates[key] = 0;
              totalTimesAdded++;
            }
          }
        }
      }
    }

    if (debug) {
      console.log('%cItems to Remove: ', 'color: #ADD8E6', updates);
    }

    $.ajax({
      type: 'POST',
      url: '/cart/update.js',
      data: updates,
      dataType: 'json',
      success: function (response) {
        if (debug) {
          console.log('%cSuccessfully Removed Items from the cart: ', 'color: #ADD8E6', response);
        }

        // //Trigger Drawer refresh
        // let rebuildCartEvent = new Event('rebuildCart' , { bubbles: "true" , cancelable: "false"});
        // window.dispatchEvent( rebuildCartEvent );
      }
    });
  });


  return;
}


function checkForWarrantySelection__buyPanel() {
  const buyPanelWarrantyPlanWrapper = document.querySelector('.warranty-plan-options');

  let warrantyID = null;

  //check the outer list for an option that is selected
  if (buyPanelWarrantyPlanWrapper !== null) {
    const selectableOptions = buyPanelWarrantyPlanWrapper.querySelectorAll('[data-plan-option]');

    if (selectableOptions != null && selectableOptions != undefined) {
      if (selectableOptions.length == 0) {
        return 0;
      }

      let temp;
      for (let i = 0; i < selectableOptions.length; i++) {
        if (selectableOptions[i].checked) {
          temp = selectableOptions[i].getAttribute('data-plan-variant-id');
          warrantyID = Number(temp);
          break;
        }
      }
    }
  }

  return warrantyID;

}

/**
 * Function will handle the logic of selecting the outer radio buttons. Check when clicked, uncheck when clicking the same one again
 * @param {Event} event - Javascript Event object
 * @returns {void}
 */
var previousClicked = null;
function handleCheckedState(event) {
  const buyPanelWarrantyPlanWrapper = document.querySelector('.warranty-plan-options');

  if (buyPanelWarrantyPlanWrapper != null && buyPanelWarrantyPlanWrapper != undefined) {
    let inputElements = buyPanelWarrantyPlanWrapper.querySelectorAll('input');
    let inputElement = this;
    // uncheck all the buttons
    for (let input of inputElements) {
      input.checked = false;
    }

    // if the previousClicked element is not the same as the current one
    if (previousClicked !== inputElement) {
      // update previous
      previousClicked = inputElement;
      //check the current element
      inputElement.checked = true;
    } else {
      // point it back to null
      previousClicked = null;
    }
  }


  return;
}

/**
 * Function handles building the radio buttons for the upsell.
 * @param {Object} warrantyResult - Result of Warranty API fetch
 * @param {Boolean} debug - Trigger for showing console.log statements
 * @returns {void} 
 */
function loadWarranyInfoIntoButtons(warrantyResult, debug) {
  if (debug) {
    console.log('loading warranty info buttons...');
  }

  try {
    toggleLoading(true);

    if (warrantyResult != null && warrantyResult != undefined) {
      if (debug) {
        console.log('Loaded Warranty Info Buttons!')
      }
      let resultAsObject = JSON.parse(warrantyResult);

      if (resultAsObject.message != 'unauthorized') {
        let warrantyPlanInfo = document.querySelector('.warranty-plan-info');
        if (warrantyPlanInfo !== null) {
          warrantyPlanInfo.classList.remove('hidden');
        }

        const buyPanelWarrantyPlanWrapper = document.querySelector('.warranty-plan-options');
        if (buyPanelWarrantyPlanWrapper !== null) {
          //Build Labels
          buyPanelWarrantyPlanWrapper.innerHTML = '';
          buildOuterLabels(resultAsObject.coverage_options, debug);
          loadDeselectionOfOuterProtectionOptions(debugLogging);
        }

        // Get all the inner upsell buttons
        const modalWarrantyPlanWrapper = document.querySelector('.protection-plan-options-wrapper');
        if (modalWarrantyPlanWrapper !== null) {
          //Build Labels 
          modalWarrantyPlanWrapper.innerHTML = '';
          buildInnerLabels(resultAsObject.coverage_options, debug);
        }
      }

    } else {
      if (debug) {
        console.log('Warranty Results were null or undefined... getting plan')
      }

      // we need to wait for the variant id to update
      const warrantyOptionsPromise = fetchWarrantyOptions();

      warrantyOptionsPromise.then(result => {
        let resultAsObject = JSON.parse(result);

        if (resultAsObject.message != 'unauthorized') {
          let warrantyPlanInfo = document.querySelector('.warranty-plan-info');
          if (warrantyPlanInfo !== null) {
            warrantyPlanInfo.classList.remove('hidden');
          }

          const buyPanelWarrantyPlanWrapper = document.querySelector('.warranty-plan-options');
          if (buyPanelWarrantyPlanWrapper !== null) {
            //Build Labels
            buyPanelWarrantyPlanWrapper.innerHTML = '';
            buildOuterLabels(resultAsObject.coverage_options, debug);
            loadDeselectionOfOuterProtectionOptions(debugLogging);
          }

          // Get all the inner upsell buttons
          const modalWarrantyPlanWrapper = document.querySelector('.protection-plan-options-wrapper');
          if (modalWarrantyPlanWrapper !== null) {
            //Build Labels 
            modalWarrantyPlanWrapper.innerHTML = '';
            buildInnerLabels(resultAsObject.coverage_options, debug);
          }
        }
      });

      addAllWarrantyOptionsToLocalStorage();
    }
  } catch (error) {
    console.log('Error loading warranty info into buttons', error)
  }


  return;
}

function toggleLoading(isLoading) {
  const coveragePlanWrapper = document.getElementById("Warranty-Coverage-Plan");
  let coveragePlanOptionsWrapper = null;
  let coveragePlanOptionsLoadingWrapper = null;
  if (coveragePlanWrapper !== null) {
    coveragePlanOptionsWrapper = coveragePlanWrapper.querySelector('.warranty-plan-options');
  }
  if (coveragePlanOptionsWrapper !== null) {
    coveragePlanOptionsLoadingWrapper = coveragePlanWrapper.querySelector('.warranty-plan-options-loading');
  }
  const addToCartButtons = document.querySelectorAll('.mobile-pixels-warranty-trigger');

  let canContinue = checkLocalStorageForWarranty();

  if (coveragePlanWrapper !== null && coveragePlanOptionsWrapper !== null && coveragePlanOptionsLoadingWrapper !== null && canContinue) {
    if (isLoading) {
      if (debugLogging) {
        console.log('currently loading...');
      }
      coveragePlanOptionsWrapper.classList.add('hidden');
      coveragePlanOptionsLoadingWrapper.classList.remove('hidden');
      // set timout is to let the tbyb logic run before we do this piece
      setTimeout(() => {
        addToCartButtons.forEach(addToCartButton => {
          addToCartButton.classList.add('sold-out');
          addToCartButton.setAttribute('disabled', 'true');
        });
      }, 0);

    } else {
      if (debugLogging) {
        console.log('done loading...');
      }
      coveragePlanOptionsWrapper.classList.remove('hidden');
      coveragePlanOptionsLoadingWrapper.classList.add('hidden');
      setTimeout(() => {
        addToCartButtons.forEach(addToCartButton => {
          addToCartButton.classList.remove('sold-out');
          addToCartButton.removeAttribute('disabled');
        });
      }, 0);
    }
  }

  return;
}

/**
 * Function will load the Open and Close Event Listners for the Information Modal
 * @returns {void}
 */
function loadInformationModal() {
  const warrantyModal = document.querySelector('#Warranty-Coverage-Plan');
  const informationModal = document.querySelector('#Warranty-Information-Modal');

  const upsellModal = document.querySelector('#Warranty-Upsell-Modal');
  if (upsellModal != null) {
    upsellModal.addEventListener('click', handleOutsideClick);
  }

  if (warrantyModal !== null) {
    const warrantyInfoCloseButton = warrantyModal.querySelector('[data-close-info-modal]');
    const openWarrantyInfoModalToggle = document.querySelector('[data-open-warranty-modal]');

    if (warrantyInfoCloseButton !== null) {
      warrantyInfoCloseButton.addEventListener('click', handleModalToggle);
    }

    if (openWarrantyInfoModalToggle !== null) {
      openWarrantyInfoModalToggle.addEventListener('click', handleModalToggle);
    }
  }

  if (informationModal !== null) {
    informationModal.addEventListener('click', handleOutsideClick);
    const infoModalCloseButton = informationModal.querySelector('[data-close-info-modal]');

    if (infoModalCloseButton !== null) {
      infoModalCloseButton.addEventListener('click', handleModalToggle);
    }
  }
}

function handleOutsideClick(event) {
  if (event.target.id == 'Warranty-Information-Modal') {
    handleModalToggle(event);
  } else if (event.target.id == 'Warranty-Upsell-Modal') {
    handleModalTriggers(event);
  }
}

/**
 * Function will load the Event Listers for The Add to Cart Conditions ( ie. with or without upsell )
 * @param {Boolean} debug - determines if console.log statements should show
 * @returns {void}
 */
function loadWarrantyModalSelectionTriggers(debug) {
  const denyButtons = document.querySelectorAll('[data-no-protection-plan]');
  const acceptPlan = document.querySelector('[data-add-protection-plan]');

  if (denyButtons != null && denyButtons != undefined) {
    for (let denyButton of denyButtons) {
      denyButton.removeEventListener('click', handleModalTriggers)
      denyButton.addEventListener('click', handleModalTriggers)

      if (debug) {
        console.log('%cAttached Event Listner to: ', 'color: #FFF720', denyButton);
      }
    }
  }

  if (acceptPlan !== null) {
    acceptPlan.removeEventListener('click', handleModalTriggers)
    acceptPlan.addEventListener('click', handleModalTriggers)


    if (debug) {
      console.log('%cAttached Event Listner to: ', 'color: #FFF720', acceptPlan);
    }
  }

  return;
}

/**
 * Function will load the Event Listers for Outer Protection Options
 * @param {Boolean} debug - determines if console.log statements should show, default false
 * @returns {void}
 */
function loadDeselectionOfOuterProtectionOptions(debug) {
  const buyPanelWarrantyPlanWrapper = document.querySelector('.warranty-plan-options');
  if (buyPanelWarrantyPlanWrapper != null && buyPanelWarrantyPlanWrapper != undefined) {
    let inputsToWatch = buyPanelWarrantyPlanWrapper.querySelectorAll('input');

    if (inputsToWatch != null && inputsToWatch != undefined) {
      for (let inputToWatch of inputsToWatch) {
        inputToWatch.addEventListener('click', handleCheckedState);
      }

      if (debug) {
        console.log('%cAttached Event Listner to: ', 'color: #FFF720', inputsToWatch);
      }
    }
  }

  return;
}



/** 
 * Function will build the redirect url for BUY NOW
 * 
 * @param {itemsArray} array - list of all the items in the array of items to send to BUY NOW
 * @returns {void}
 */
function runBuyNowRedirect(itemsArray) {
  let redirectURL = '';

  itemsArray.forEach(item => {
    if (redirectURL != '') {
      redirectURL += ',';
    }
    redirectURL += `${item.id}:${item.quantity}`
  })

  document.location.href = `/cart/${redirectURL}`;
}

/*******************************************************************************************
 * 
 *   The below function handle the cart functionality 
 * 
 ******************************************************************************************/


function handleWarrantyUpsellInCart(cart_items) {

  let cartItems = [];
  for (let i = 0; i < cart_items.length; i++) {
    let itemIsABundleItem = false;
    let childOfBundle = false;
    let linkedId = null;
    let properties = cart_items[i].properties;
    if (properties !== null) {
      if ("_linked_id " in properties) {
        itemIsABundleItem = true;
        linkedId = properties._linked_id;
      }

      if ('_is_mp_warranty' in properties) {
        childOfBundle = true;
      }


    }
    let item = {
      discounted_price: cart_items[i].discounted_price,
      discounts: cart_items[i].discounts,
      featured_image: cart_items[i].featured_image,
      final_line_price: cart_items[i].final_line_price,
      final_price: cart_items[i].final_price,
      gift_card: cart_items[i].gift_card,
      grams: cart_items[i].grams,
      handle: cart_items[i].handle,
      has_components: cart_items[i].has_components,
      id: cart_items[i].id,
      image: cart_items[i].image,
      key: cart_items[i].key,
      line_level_discount_allocations: cart_items[i].line_level_discount_allocations,
      line_level_total_discount: cart_items[i].line_level_total_discount,
      line_price: cart_items[i].line_price,
      options_with_values: cart_items[i].options_with_values,
      original_line_price: cart_items[i].original_line_price,
      original_price: cart_items[i].original_price,
      price: cart_items[i].price,
      product_description: cart_items[i].product_description,
      product_has_only_default_variant: cart_items[i].product_has_only_default_variant,
      product_id: cart_items[i].product_id,
      product_title: cart_items[i].product_title,
      product_type: cart_items[i].product_type,
      properties: cart_items[i].properties,
      quantity: cart_items[i].quantity,
      quantity_rule: cart_items[i].quantity_rule,
      requires_shipping: cart_items[i].requires_shipping,
      sku: cart_items[i].sku,
      taxable: cart_items[i].taxable,
      title: cart_items[i].title,
      total_discount: cart_items[i].total_discount,
      url: cart_items[i].url,
      variant_id: cart_items[i].variant_id,
      variant_options: cart_items[i].variant_options,
      variant_title: cart_items[i].variant_title,
      vendor: cart_items[i].vendor,
      isBundle: itemIsABundleItem,
      itemBundleId: linkedId,
      isChildOfBundle: childOfBundle,
    }

    cartItems.push(item)
  }


  const lineItemWithWarranties = [];
  const itemIsWarranty = [];
  const itemsWithoutWarranty = [];

  // find warranty items in cart
  for (let i = 0; i < cartItems.length; i++) {
    // console.log('debugging: cart item:', cartItems[i])
    if (cartItems[i].vendor == "Mobile Pixels Warranty" && cartItems[i].properties._is_mp_warranty) {
      itemIsWarranty.push({ id: cartItems[i].properties._linked_id, qty: cartItems[i].quantity });
    }
  }

  // find products that do not have a matching warranty in cart
  for (let i = 0; i < cartItems.length; i++) {
    let match = false;

    // console.log('debugging: warranties length', itemIsWarranty.length)
    for (let j = 0; j < itemIsWarranty.length; j++) {
      if (cartItems[i].variantId == itemIsWarranty[j].id) {
        match = true;

        if (cartItems[i].itemQty != itemIsWarranty[j].qty) {
          match = false;
        }
      }
    }

    if (!match) {
      if ('_is_mp_warranty' in cartItems[i].properties && cartItems[i].properties._is_mp_warranty) {
        // item is a warranty, do nothing
      } else {
        itemsWithoutWarranty.push(cartItems[i]);
      }
    } else {
      lineItemWithWarranties.push(cartItems[i]);
    }
  }

  // console.log('debugging: is warranty', itemIsWarranty)
  // console.log("debugging: without warranty", itemsWithoutWarranty)
  // console.log("debugging: with warranty", lineItemWithWarranties)

  // for every item without a warranty
  for (let i = 0; i < itemsWithoutWarranty.length; i++) {
    // console.log("Item without warranties: ", itemsWithoutWarranty[i])
    // Only look for warranty data and create cart upsell buttons for products that are not warranties
    if (itemsWithoutWarranty[i].vendor != "Mobile Pixels Warranty") {
      // console.log('debugging: item without warranty', itemsWithoutWarranty[i])
      // check if warranty data is in local storage, if not fetch data and add to local storage
      const warrantiesInStorage = localStorage.getItem('warranties');
      if (warrantiesInStorage !== null) {
        const properties = {};

        if (itemsWithoutWarranty.isBundle) {
          properties._linked_id = itemsWithoutWarranty[i].itemBundleId
        } else {
          properties._linked_id = itemsWithoutWarranty[i].id
        }
        if (itemsWithoutWarranty.isChildOfBundle) {
          properties._is_mp_warranty = true;
        }
        if (itemsWithoutWarranty[i].quantity > 0) {
          properties.quantity = itemsWithoutWarranty[i].quantity;
        }
        // console.log('yo debugging:', !warrantiesInStorage.includes( itemsWithoutWarranty[i].variantId ))
        if (!warrantiesInStorage.includes(itemsWithoutWarranty[i].variant_id)) {
          // console.log( 'Missing Warranty for this variant...' );
          fetchWarrantyData(itemsWithoutWarranty[i]);
        } else {
          // console.log( 'Warranty info was found and we are now able to build the button!' );
          //console.log( 'force the callback function to trigger the build logic...' );
        }
      } else {
        //   console.log( 'No Warranty Data found in local storage...' );
        fetchWarrantyData(itemsWithoutWarranty[i]);
      }
    }
  }
}


async function fetchWarrantyData(item) {
  let APIUrl = `/a/warranty/product-database-product/${item.productId}/variant/${item.variantId}`;

  // Make a call to the API
  var myHeaders = new Headers();
  var authHeaderString = `mobilepixels.myshopify.com:${API_LOGIN_KEY}`;
  myHeaders.append("Authorization", authHeaderString);

  var requestOptions = {
    method: 'GET',
    headers: myHeaders
  };

  const response = await fetch(APIUrl, requestOptions);
  const results = await response.json();

  if (results.coverage_options.length > 0) {
    const localStorageWarranty = localStorage.getItem('warranties');
    if (localStorageWarranty !== null) {
      const jsonItems = JSON.parse(localStorageWarranty);
      const newItemsForStorage = [...jsonItems, results];
      localStorage.setItem('warranties', JSON.stringify(newItemsForStorage));
    } else {
      const tempArr = [];
      tempArr.push(results)
      localStorage.setItem('warranties', JSON.stringify(tempArr))
    }
  }

  return;
}

function checkLocalStorageForWarranty() {
  const localStorageWarranty = localStorage.getItem('warranties');
  const currentVariantId = window._productDetails.currently_selected_variant_id;

  if (localStorageWarranty && localStorageWarranty.includes(currentVariantId.toString())) {
    return true
  } else {
    return false
  }
}

function handleWarrantyButton(variant_id) {
  // get warrantyData from local storage 

  const warrantiesInStorage = localStorage.getItem('warranties');

  if (warrantiesInStorage != null) {
    if (warrantiesInStorage.includes(variant_id)) {
      let warrantyData = null;
      let warrantyStorageArray = JSON.parse(warrantiesInStorage);

      // loop over warranties in local storage
      let foundWarrantyData = false;
      for (let t = 0; t < warrantyStorageArray.length; t++) {
        // loop over coverage option in each warranty to look for warranties for the cart item
        for (let w = 0; w < warrantyStorageArray[t].coverage_options.length; w++) {
          let optionToCheck = warrantyStorageArray[t].coverage_options[w];
          if (optionToCheck.name.includes(variant_id)) {
            warrantyData = warrantyStorageArray[t];
            foundWarrantyData = true;
            break;
          }
        }
        if (foundWarrantyData) {
          break;
        }
      }

      // console.log('debugging found warranty data', foundWarrantyData)
      // console.log('debugging warranty data', warrantyData)
      buildModalData(warrantyData);
    }
  }

  // console.log( 'Triggering Warranty Wrapper Event!');

  // open modal and pass modal warranty into
  let warrantyModal = document.querySelector("#Warranty-Upsell-Modal");

  //add new data attribute to seperate modal functions from cart flyout modal functions & remove other attribute, if present
  warrantyModal.setAttribute('data-opened-from-cart', variant_id);
  if (warrantyModal.hasAttribute('data-opened-from-atc')) {
    warrantyModal.removeAttribute('data-opened-from-atc');
  }

  //Show modal
  warrantyModal.classList.remove('closed');

  return;
}

function buildModalData(warrantyData) {
  const coverageOptions = warrantyData.coverage_options;

  coverageOptions.sort((a, b) => {
    let trueLengthA = parseInt(a.plan_length.split(' ')[0]);
    let trueLengthB = parseInt(b.plan_length.split(' ')[0]);

    if (trueLengthA > trueLengthB) {
      return 1;
    }
    else {
      return -1;
    }
  });

  const uniqueCoverageOptions = [];
  let currentlength = coverageOptions[0].plan_length;
  uniqueCoverageOptions.push(coverageOptions[0]);

  for (let u = 0; u < coverageOptions.length; u++) {
    if (coverageOptions[u].plan_length[0] == currentlength[0]) {

    } else {
      uniqueCoverageOptions.push(coverageOptions[u]);
      currentlength = coverageOptions[u].plan_length;
    }
  }

  const warrantyPlanOptions = document.querySelector('.protection-plan-options-wrapper');

  if (warrantyPlanOptions) {
    while (warrantyPlanOptions.firstChild) {
      warrantyPlanOptions.removeChild(warrantyPlanOptions.firstChild)
    };
  }

  for (let i = 0; i < uniqueCoverageOptions.length; i++) {
    // Label for input element
    let labelElement = document.createElement('label');
    labelElement.setAttribute('for', `warranty-upsell-option-${i}`);

    //Input Element
    let inputElement = document.createElement('input');
    inputElement.setAttribute('name', 'warranty-upsell');
    inputElement.setAttribute('id', `warranty-upsell-option-${i}`);
    inputElement.value = "";
    inputElement.setAttribute('type', 'radio');
    inputElement.setAttribute('data-plan-option', uniqueCoverageOptions[i].title);
    inputElement.setAttribute('data-plan-variant-id', uniqueCoverageOptions[i].id);
    inputElement.setAttribute('data-plan-product-id', uniqueCoverageOptions[i].productId);
    inputElement.setAttribute('data-length', uniqueCoverageOptions[i].plan_length);

    if (i == Math.round((uniqueCoverageOptions.length - 1) / 2)) {
      inputElement.setAttribute('checked', '');
    }

    //Content Wrapper
    let planDetailsWrapper = document.createElement('div');
    planDetailsWrapper.classList.add('warranty-upsell-button-content');

    // Length
    let planLengthElement = document.createElement('div');
    planLengthElement.classList.add('plan-length');
    planLengthElement.innerHTML = uniqueCoverageOptions[i].plan_length;

    //Plan Type 
    let planType = document.createElement('div');
    planType.classList.add('plan-type');
    planType.innerHTML = 'Protection Plan';

    //Price 
    let planPriceElement = document.createElement('div');
    planPriceElement.classList.add('plan-price');
    planPriceElement.innerHTML = '$' + uniqueCoverageOptions[i].plan_price;

    // Badge - <div class="badge">Best Seller</div> | <div class="badge">Best Value</div>
    // Add Best Seller to middle option
    // Add Best Value to last option
    let badgeElement = document.createElement('div');
    badgeElement.classList.add('badge');

    if (i == Math.round((uniqueCoverageOptions.length - 1) / 2)) {
      badgeElement.innerHTML = 'Best Seller';
    }

    if (i == uniqueCoverageOptions.length - 1) {
      badgeElement.innerHTML = 'Best Value';
    }

    //combine details wrapper
    planDetailsWrapper.append(planLengthElement);
    planDetailsWrapper.append(planType);
    planDetailsWrapper.append(planPriceElement);

    // if we have a badge with content, add it
    if (badgeElement.innerHTML != '') {
      planDetailsWrapper.append(badgeElement);
    }

    //combine label
    labelElement.append(inputElement);
    labelElement.append(planDetailsWrapper);

    //append label element
    warrantyPlanOptions.append(labelElement);

  }
}


// need to access this function from theme.js.liquid.  It can only access using the window object
window.loadWarranyInfoIntoButtons = loadWarranyInfoIntoButtons
window.handleWarrantyUpsellInCart = handleWarrantyUpsellInCart
window.handleWarrantyButton = handleWarrantyButton
window.fetchWarrantyOptions = fetchWarrantyOptions
window.checkForWarrantySelectionOnPDP = checkForWarrantySelection__buyPanel;
window.loadWarrantyModalSelectionTriggers = loadWarrantyModalSelectionTriggers;